import { serverReportError } from '#app/lib/error'
import { warn } from '#app/lib/loggers'
import { wait } from '#app/lib/promises'

// Keep in sync with app/modules/general/scss/_media_query_thresholds.scss
const wellknownWidths = {
  '$small-screen': 1000,
  '$smaller-screen': 600,
  '$very-small-screen': 400,
} as const

export type WellknownWidth = keyof typeof wellknownWidths

const resolveWidth = (width: number | WellknownWidth) => wellknownWidths[width] ? wellknownWidths[width] : width

// window.visualViewport is being polyfilled (see app/init_polyfills.js)
// but errors can still be found in the logs
export const getViewportWidth = () => window.visualViewport?.width || window.screen.width
export const getViewportHeight = () => window.visualViewport?.height || window.screen.height

export const viewportIsSmallerThan = (maxWidth: number | WellknownWidth) => getViewportWidth() < resolveWidth(maxWidth)
export const viewportIsLargerThan = (maxWidth: number | WellknownWidth) => getViewportWidth() >= resolveWidth(maxWidth)

export const viewportIsSmall = () => viewportIsSmallerThan('$small-screen')

interface ScrollOptions {
  marginTop?: number
  waitForRoomToScroll?: boolean
  highlight?: boolean
  focus?: boolean
}

export function scrollToElement (element: HTMLElement | undefined, options: ScrollOptions = {}) {
  if (!(element instanceof HTMLElement)) {
    serverReportError('invalid element', { element, options })
    // Assumes that a failing scroll is never worth crashing the calling function
    return
  }

  const { marginTop = 0, waitForRoomToScroll = true, highlight = false, focus = false } = options

  let attempts = 0
  const attemptToScroll = () => {
    const offset = element.offsetTop
    const viewportHeight = getViewportHeight()
    const bodyHeight = window.document.body.clientHeight
    const hasRoomToScroll = (viewportHeight + offset < bodyHeight)
    if (!waitForRoomToScroll || hasRoomToScroll || ++attempts > 6) {
      // Not using element.scrollIntoView to be able to set an arbitrary marginTop
      window.scrollTo({
        top: Math.max(0, offset - marginTop),
        behavior: 'smooth',
      })
      if (highlight) {
        element.classList.add('post-scroll-highlight')
        setTimeout(() => element.classList.remove('post-scroll-highlight'), 1000)
      }
      if (focus) focusElement(element)
    } else {
      setTimeout(attemptToScroll, 50)
    }
  }

  setTimeout(attemptToScroll, 50)
}

interface ScrollToSelectorOptions extends ScrollOptions {
  retryDelay?: number
  maxAttempts?: number
}

export async function scrollToSelector (selector: string, options: ScrollToSelectorOptions = {}) {
  const { retryDelay = 100, maxAttempts = 50 } = options
  let attempts = 0
  while (++attempts < maxAttempts) {
    const target = document.querySelector(selector)
    if (target) {
      return scrollToElement(target as HTMLElement, options)
    } else {
      await wait(retryDelay)
    }
  }
  warn(`could not find scroll selector ${selector} target, after ${attempts} attempts and waiting ${retryDelay * attempts}ms`)
}

export const onScrollToBottom = (action, limitBeforeBottom = 100) => e => {
  const { scrollTop, scrollTopMax } = e.currentTarget
  if (scrollTopMax < limitBeforeBottom) return
  if (scrollTop + limitBeforeBottom > scrollTopMax) action()
}

export function focusElement (element: HTMLElement) {
  // Make sure the element is focusable
  // See https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/tabindex
  element.tabIndex = 0
  element.focus({ preventScroll: true })
}
